import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldAlert, Bandage } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ReportFieldInput } from "./ReportFieldInput";

export function NewReportDialog({ open, onClose, templates, teams = [], onCreated, prefill = null }) {
  const [type, setType] = useState("accident");
  const [values, setValues] = useState({});
  const [teamId, setTeamId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setType(prefill?.type || "accident");
    setValues(prefill?.values || {});
    setTeamId(prefill?.team_id || "");
  }, [open, prefill]);
  const fields = templates?.[type] || [];
  const missing = fields.filter(f => f.required && !(values[f.key] ?? "").toString().trim());

  const submit = async () => {
    if (missing.length) { toast.error(`Please complete: ${missing.map(f => f.label).join(", ")}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/incidents", { type, fields: values, team_id: teamId || null, booking_id: prefill?.booking_id || null });
      toast.success(`${type === "accident" ? "Accident" : "Incident"} report ${data.ref} submitted — reviewers alerted`);
      onCreated?.(data); onClose?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit report"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="new-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">Report an accident or incident</DialogTitle>
          <DialogDescription className="text-xs opacity-70">Accidents go to the club admins and Welfare Officer. Incidents (safeguarding / behaviour concerns) go to the Club Welfare Officer only.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="grid grid-cols-2 gap-2" data-testid="report-type-picker">
            {[{ v: "accident", l: "Accident", d: "Injury during play or training", I: Bandage }, { v: "incident", l: "Incident", d: "Safeguarding / welfare concern (CWO)", I: ShieldAlert }].map(o => (
              <button key={o.v} type="button" onClick={() => { setType(o.v); setValues(prefill?.values ? { ...prefill.values } : {}); }} data-testid={`report-type-${o.v}`}
                className={`text-left p-3 rounded-md border transition-colors ${type === o.v ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-white/40"}`}>
                <div className="flex items-center gap-2 font-display font-bold"><o.I className="w-4 h-4 text-[#83B68B]" /> {o.l}</div>
                <div className="text-[11px] opacity-60 mt-0.5">{o.d}</div>
              </button>
            ))}
          </div>
          {teams.length > 0 && (
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Team (optional)</Label>
              <Select value={teamId || "none"} onValueChange={v => setTeamId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="report-team" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— not team specific —</SelectItem>{teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.age_group}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map(f => <ReportFieldInput key={f.key} field={f} value={values[f.key]} onChange={v => setValues(cur => ({ ...cur, [f.key]: v }))} />)}
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40" data-testid="report-cancel">Cancel</button>
            <button type="button" onClick={submit} disabled={busy} data-testid="report-submit" className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40">{busy ? "Submitting…" : "Submit report"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
